<template>
  <div>
    <v-slide-y-transition>
      <v-card
        v-if="selectedPendingRows.length"
        class="mb-3 bulk-actions-bar"
        color="#f3e5f5"
        flat
      >
        <v-card-text class="d-flex align-center flex-wrap py-3">
          <v-chip color="primary" text-color="white" class="ml-3">
            تم تحديد {{ selectedPendingRows.length }} عنصر
          </v-chip>
          <v-spacer />
          <v-btn
            color="primary"
            :loading="converting"
            :disabled="converting"
            class="ml-2"
            @click="$emit('convert-selected')"
          >
            <v-icon left small>mdi-account-convert</v-icon>
            تحويل إلى عملاء وطلبات
          </v-btn>
          <!-- ============== Reject Selected Button ============== -->
          <v-btn
            color="error"
            :loading="rejecting"
            :disabled="rejecting"
            class="ml-2"
            @click="$emit('reject-selected')"
          >
            <v-icon left small>mdi-close-circle</v-icon>
            رفض الطلبات
          </v-btn>
          <!-- ============== End Reject Selected Button ============== -->

          <v-btn outlined @click="$emit('clear-selection')">
            إلغاء التحديد
          </v-btn>
        </v-card-text>
      </v-card>
    </v-slide-y-transition>

    <table-component
      :key="`funding-submissions-${statusFilter}`"
      :showSelect="allowRowSelect"
      :item-disabled="isRowDisabled"
      itemKey="id"
      :selectedRows.sync="localSelectedRows"
      :headers="tableHeaders"
      :pagination="tablePagination"
      :limit="$store.state.counterOfShow"
      :items="items"
      :loading="loading"
      :noDataText="emptyMessage"
      v-model="localPage"
      @refresh="$emit('refresh')"
    >
      <template v-slot:[`item.row_num`]="{ index }">
        {{ rowNumber(index) }}
      </template>

      <template v-slot:[`item.name`]="{ item }">
        {{ item.name || "-" }}
      </template>

      <template v-slot:[`item.mobile`]="{ item }">
        <span dir="ltr">{{ formatSaudiMobile(item.mobile) }}</span>
      </template>

      <template v-slot:[`item.email`]="{ item }">
        {{ item.email || "-" }}
      </template>

      <template v-slot:[`item.product_type`]="{ item }">
        {{ productTypeLabel(item) }}
      </template>

      <template v-slot:[`item.salary`]="{ item }">
        {{ item.salary || "-" }}
      </template>

      <template v-slot:[`item.is_verified`]="{ item }">
        <v-chip
          small
          :color="item.is_verified ? 'success' : 'orange darken-1'"
          text-color="white"
        >
          {{ item.is_verified ? "مفعل" : "غير مفعل" }}
        </v-chip>
      </template>

      <template v-slot:[`item.created_at`]="{ item }">
        {{ formatDateTime(item.created_at) }}
      </template>

      <template v-slot:[`item.call_time`]="{ item }">
        {{ item.call_time ? callTimeItems(item.call_time) : "-" }}
      </template>

      <template v-slot:[`item.status`]="{ item }">
        <v-chip small :color="statusColor(item.status)" text-color="white">
          {{ statusLabel(item.status) }}
        </v-chip>
      </template>

      <template v-slot:[`item.actions`]="{ item }">
        <template v-if="item.status === 'pending'">
          <v-btn
            small
            color="primary"
            depressed
            :disabled="converting"
            @click="$emit('convert-single', item.id)"
          >
            تحويل
          </v-btn>
          <v-btn
            small
            color="error"
            class="mr-2"
            depressed
            :disabled="rejecting"
            @click="$emit('reject-single', item.id)"
          >
            رفض
          </v-btn>
        </template>
        <span v-else>-</span>
      </template>
    </table-component>
  </div>
</template>

<script>
import {
  formatSaudiMobile,
  formatDateTime,
  productTypeLabel,
  statusLabel,
  statusColor,
  isPendingSubmission,
} from "@/utils/fundingFormSubmissions";

const BASE_HEADERS = [
  { text: "#", value: "row_num", width: "70px", sortable: false },
  { text: "الاسم", value: "name", sortable: false },
  { text: "الجوال", value: "mobile", sortable: false },
  { text: "البريد", value: "email", sortable: false },
  { text: "نوع المنتج", value: "product_type", sortable: false },
  { text: "الراتب", value: "salary", sortable: false },
  { text: "تفعيل الجوال", value: "is_verified", sortable: false, align: "center" },
  { text: "تاريخ الإرسال", value: "created_at", sortable: false },
  { text: "الوقت المناسب للتواصل", value: "call_time", sortable: false },
  { text: "الحالة", value: "status", sortable: false, align: "center" },
  { text: "إجراءات", value: "actions", sortable: false, align: "center" },
];

export default {
  name: "SubmissionsTable",
  props: {
    items: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    converting: { type: Boolean, default: false },
    rejecting: { type: Boolean, default: false },
    selectedRows: { type: Array, default: () => [] },
    pagination: { type: Object, required: true },
    page: { type: Number, default: 1 },
    statusFilter: { type: String, default: "all" },
  },
  computed: {
    allowRowSelect() {
      return this.statusFilter !== "rejected";
    },
    selectedPendingRows() {
      return this.selectedRows.filter(isPendingSubmission);
    },
    tableHeaders() {
      return BASE_HEADERS;
    },
    emptyMessage() {
      switch (this.statusFilter) {
        case "pending":
          return "لا توجد طلبات قيد المراجعة حالياً";
        case "rejected":
          return "لا توجد طلبات مرفوضة حالياً";
        default:
          return "لا توجد طلبات حالياً";
      }
    },
    tablePagination() {
      return {
        limit: this.$store.state.counterOfShow,
        count: this.pagination.total,
      };
    },
    localSelectedRows: {
      get() {
        const visibleIds = new Set(this.items.map((item) => item.id));
        return this.selectedRows.filter(
          (row) => visibleIds.has(row.id) && isPendingSubmission(row)
        );
      },
      set(val) {
        this.$emit("update:selectedRows", val.filter(isPendingSubmission));
      },
    },
    localPage: {
      get() {
        return this.page;
      },
      set(val) {
        this.$emit("update:page", val);
      },
    },
  },
  watch: {
    statusFilter() {
      this.$emit("update:selectedRows", []);
    },
    items() {
      const visibleIds = new Set(this.items.map((item) => item.id));
      const visibleSelection = this.selectedRows.filter(
        (row) => visibleIds.has(row.id) && isPendingSubmission(row)
      );
      if (visibleSelection.length !== this.selectedRows.length) {
        this.$emit("update:selectedRows", visibleSelection);
      }
    },
  },
  methods: {
    formatSaudiMobile,
    formatDateTime,
    productTypeLabel,
    statusLabel,
    statusColor,
    isRowDisabled(item) {
      return !isPendingSubmission(item);
    },
    rowNumber(index) {
      if (this.pagination.from > 0) {
        return this.pagination.from + index;
      }
      return (this.page - 1) * this.$store.state.counterOfShow + index + 1;
    },
    callTimeItems(call_time) {
      const items = [
        {id:'before_noon', value:'قبل الظهر'},
        {id:'after_noon', value:'بعد الظهر'},
        {id:'after_asr', value:'بعد العصر'}
      ];
      return items.find(item => item.id === call_time)?.value;
    },
  },
};
</script>

<style scoped>
.bulk-actions-bar {
  border: 1px solid #e1bee7;
}
</style>
